import { useEffect, useState, useCallback, useMemo } from "react";
import { API_BASE } from "../lib/api";
import { Thumbnail } from "../components/Thumbnail";
import { adminOverrideSegmentSpeaker, getSuggestionCount } from "../lib/api/speaker-override";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;
const SPEAKER_OVERRIDE_ENABLED = (import.meta as any).env?.VITE_FEATURE_SEGMENT_SPEAKER_OVERRIDE === 'true';

interface SpeakerFlag {
  id: number;
  item_id: string;
  original_speaker: string;
  corrected_speaker: string;
  confidence: number | null;
  failure_mode: string;
  evidence: string | null;
  source: string;
  created_at: string | null;
  title: string;
  channel_name: string | null;
  video_id: string | null;
  thumbnail_url: string | null;
  first_appearance_ms: number | null;
  reviewed?: boolean;
  auto_fixed?: boolean;
  segment_id?: string | null; // Populated for per-segment rows (user_suggestion, manual_segment_override)
}

const FAILURE_MODE_COLORS: Record<string, string> = {
  wrong_name: "bg-red-100 text-red-700",
  generic_role: "bg-red-100 text-red-700",
  first_name_only: "bg-amber-100 text-amber-700",
  misspelling: "bg-amber-100 text-amber-700",
  title_fragment: "bg-amber-100 text-amber-700",
  ad_leak_label: "bg-red-100 text-red-700",
  unnamed: "bg-blue-100 text-blue-700",
  other: "bg-gray-100 text-gray-700",
};

function formatTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export default function AdminSpeakerReviewView() {
  const [flags, setFlags] = useState<SpeakerFlag[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [channelFilter, setChannelFilter] = useState<string>("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [resolving, setResolving] = useState<Record<number, boolean>>({});
  const [coverageFlags, setCoverageFlags] = useState<SpeakerFlag[]>([]);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageLoading, setCoverageLoading] = useState(false);
  // Speaker override state (feature-flag-gated)
  const [overrideValues, setOverrideValues] = useState<Record<number, string>>({});
  const [overriding, setOverriding] = useState<Record<number, boolean>>({});
  const [suggestionCounts, setSuggestionCounts] = useState<Record<number, number>>({});
  const [dismissReason, setDismissReason] = useState<
    Record<number, "vera_wrong" | "speaker_unidentifiable" | "redundant" | "other">
  >({});
  // Approve-with-override state (unconditional — not feature-flag-gated)
  const [approveOverrideValues, setApproveOverrideValues] = useState<Record<number, string>>({});

  const { sinceParam, untilParam } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return { sinceParam: params.get("since"), untilParam: params.get("until") };
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (sourceFilter) params.set("source", sourceFilter);
      if (channelFilter) params.set("channel", channelFilter);

      const res = await fetch(`${API_BASE}/admin/speaker-audit/queue?${params}`, {
        headers: { "X-Admin-Key": ADMIN_KEY || "" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFlags(data.flags || []);
      setTotal(data.total || 0);
    } catch (err) {
      showToast("Failed to load speaker flags");
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, channelFilter]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  useEffect(() => {
    if (!sinceParam || !untilParam) return;
    setCoverageLoading(true);
    const params = new URLSearchParams({
      mode: "coverage",
      source: "vera",
      since: sinceParam,
      until: untilParam,
      limit: "200",
    });
    fetch(`${API_BASE}/admin/speaker-audit/queue?${params}`, {
      headers: { "X-Admin-Key": ADMIN_KEY || "" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setCoverageFlags(data.flags || []))
      .catch(() => showToast("Failed to load coverage data"))
      .finally(() => setCoverageLoading(false));
  }, [sinceParam, untilParam]);

  const resolveFlag = async (
    flagId: number,
    action: "approve" | "dismiss",
    dismissReason?: "vera_wrong" | "speaker_unidentifiable" | "redundant" | "other",
    approveOverride?: string,
  ) => {
    setResolving((r) => ({ ...r, [flagId]: true }));
    try {
      const trimmedOverride = approveOverride?.trim();
      const res = await fetch(`${API_BASE}/admin/speaker-audit/${flagId}/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Key": ADMIN_KEY || "",
        },
        body: JSON.stringify(
          action === "dismiss" && dismissReason
            ? { action, dismiss_reason: dismissReason }
            : action === "approve" && trimmedOverride
            ? { action, corrected_speaker_override: trimmedOverride }
            : { action },
        ),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (action === "approve") {
        showToast(`Approved — ${data.segments_updated} segment(s) updated`);
        setApproveOverrideValues((v) => { const next = { ...v }; delete next[flagId]; return next; });
      } else {
        showToast("Dismissed");
      }
      setFlags((prev) => prev.filter((f) => f.id !== flagId));
      setTotal((t) => t - 1);
    } catch (err) {
      showToast(`Failed to ${action} flag`);
    } finally {
      setResolving((r) => ({ ...r, [flagId]: false }));
    }
  };

  // Fetch suggestion counts for user_suggestion rows (non-critical — fires once per flags load)
  const flagsKey = flags.map(f => f.id).join(',');
  useEffect(() => {
    if (!SPEAKER_OVERRIDE_ENABLED) return;
    const userSuggestionFlags = flags.filter(f => f.source === 'user_suggestion');
    if (userSuggestionFlags.length === 0) return;
    Promise.all(
      userSuggestionFlags.map(f =>
        getSuggestionCount(f.id, ADMIN_KEY || '').then(r => ({ id: f.id, count: r.suggestion_count }))
      )
    ).then(results => {
      setSuggestionCounts(prev => {
        const next = { ...prev };
        results.forEach(r => { next[r.id] = r.count; });
        return next;
      });
    }).catch(() => {}); // Non-critical
  }, [flagsKey]);

  const adminDirectOverride = async (flag: SpeakerFlag) => {
    const newSpeaker = overrideValues[flag.id]?.trim();
    if (!newSpeaker || !flag.segment_id) return;
    setOverriding(o => ({ ...o, [flag.id]: true }));
    try {
      const result = await adminOverrideSegmentSpeaker(flag.segment_id, newSpeaker, ADMIN_KEY || '');
      showToast(`Replaced \u2014 ${result.segments_updated} segment(s) updated, ${result.user_overrides_cleared} override(s) cleared`);
      setFlags(prev => prev.filter(f => f.id !== flag.id));
      setTotal(t => t - 1);
    } catch {
      showToast('Failed to apply direct override');
    } finally {
      setOverriding(o => ({ ...o, [flag.id]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Speaker Review Queue</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? "Loading…" : `${total} unreviewed flag${total !== 1 ? "s" : ""}`}
          </p>
        </div>
        <button
          onClick={fetchFlags}
          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All sources</option>
          <option value="vera">Vera</option>
          <option value="batch_audit">Batch audit</option>
          <option value="user_suggestion">User suggestions</option>
        </select>
        <input
          type="text"
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          placeholder="Filter by channel…"
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">
          {toastMessage}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-sm text-gray-500 py-8 text-center">Loading flags…</div>
      ) : flags.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium">Queue is clear</p>
          <p className="text-sm text-gray-500 mt-1">No unreviewed speaker flags</p>
        </div>
      ) : (
        <div className="space-y-3">
          {flags.map((flag) => (
            <div
              key={flag.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex gap-4 shadow-sm"
            >
              {/* Thumbnail */}
              <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-gray-100">
                <Thumbnail
                  src={flag.thumbnail_url}
                  alt={flag.title ?? ""}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <a
                        href={`/transcript/${flag.item_id}${flag.first_appearance_ms ? `?t=${flag.first_appearance_ms}` : ""}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline truncate block"
                      >
                        {flag.title}
                      </a>
                      {flag.first_appearance_ms != null && (
                        <a
                          href={`/transcript/${flag.item_id}?t=${flag.first_appearance_ms}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 text-xs font-mono text-gray-400 hover:text-indigo-600 bg-gray-100 hover:bg-indigo-50 px-1.5 py-0.5 rounded"
                          title="Jump to first appearance in transcript"
                        >
                          {formatTimestamp(flag.first_appearance_ms)}
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{flag.channel_name || "Unknown channel"}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        FAILURE_MODE_COLORS[flag.failure_mode] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {flag.failure_mode.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs text-gray-400">{flag.source}</span>
                    {/* User suggestion count badge */}
                    {SPEAKER_OVERRIDE_ENABLED && flag.source === 'user_suggestion' && suggestionCounts[flag.id] != null && (
                      <span className="text-xs font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                        {suggestionCounts[flag.id]} user{suggestionCounts[flag.id] !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Speaker change */}
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-red-600 font-mono bg-red-50 px-1.5 py-0.5 rounded">
                    {flag.original_speaker}
                  </span>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="text-green-700 font-mono bg-green-50 px-1.5 py-0.5 rounded">
                    {flag.corrected_speaker}
                  </span>
                  {flag.confidence !== null && (
                    <span className="text-xs text-gray-400 ml-1">
                      {(flag.confidence * 100).toFixed(0)}% confidence
                    </span>
                  )}
                </div>

                {flag.evidence && (
                  <p className="mt-1.5 text-xs text-gray-500 italic truncate">{flag.evidence}</p>
                )}

                {/* Replace with X -- segment-scoped direct override (feature-flag-gated) */}
                {SPEAKER_OVERRIDE_ENABLED && flag.segment_id && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={overrideValues[flag.id] || ''}
                      onChange={(e) => setOverrideValues(v => ({ ...v, [flag.id]: e.target.value }))}
                      placeholder="Replace with…"
                      className="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    <button
                      onClick={() => adminDirectOverride(flag)}
                      disabled={overriding[flag.id] || !overrideValues[flag.id]?.trim()}
                      className="text-xs font-medium px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50"
                    >
                      {overriding[flag.id] ? 'Replacing…' : 'Replace'}
                    </button>
                  </div>
                )}
                {/* Actions */}
                <div className="mt-3 flex gap-2 items-center">
                  <input
                    type="text"
                    value={approveOverrideValues[flag.id] || ''}
                    onChange={(e) => setApproveOverrideValues(v => ({ ...v, [flag.id]: e.target.value }))}
                    maxLength={200}
                    placeholder="Override speaker (optional)…"
                    className="text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                  <button
                    onClick={() => resolveFlag(flag.id, "approve", undefined, approveOverrideValues[flag.id])}
                    disabled={resolving[flag.id]}
                    className={`text-xs font-medium px-3 py-1.5 ${approveOverrideValues[flag.id]?.trim() ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-md disabled:opacity-50`}
                  >
                    {resolving[flag.id] ? "Saving…" : "Approve"}
                  </button>
                  <select
                    value={dismissReason[flag.id] ?? "vera_wrong"}
                    onChange={(e) =>
                      setDismissReason((r) => ({
                        ...r,
                        [flag.id]: e.target.value as "vera_wrong" | "speaker_unidentifiable" | "redundant" | "other",
                      }))
                    }
                    disabled={resolving[flag.id]}
                    className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    <option value="vera_wrong">Vera was wrong</option>
                    <option value="speaker_unidentifiable">Speaker unidentifiable</option>
                    <option value="redundant">Redundant</option>
                    <option value="other">Other</option>
                  </select>
                  <button
                    onClick={() => resolveFlag(flag.id, "dismiss", dismissReason[flag.id] ?? "vera_wrong")}
                    disabled={resolving[flag.id]}
                    className="text-xs font-medium px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Coverage section — read-only, renders only when ?since= and ?until= are in URL */}
      {sinceParam && untilParam && (
        <section data-testid="coverage-section" className="mt-2">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setCoverageOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
            >
              <span className="text-sm font-medium text-gray-900">
                {coverageLoading
                  ? "Coverage — loading…"
                  : `Coverage — ${coverageFlags.length} item${coverageFlags.length !== 1 ? "s" : ""} audited in this batch`}
                {!coverageLoading && (
                  <span className="font-normal text-gray-500 ml-1 text-xs">
                    ({sinceParam} → {untilParam})
                  </span>
                )}
              </span>
              <svg
                className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${coverageOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {coverageOpen && (
              <div className="overflow-x-auto">
                {coverageLoading ? (
                  <div className="text-sm text-gray-500 py-6 text-center">Loading coverage data…</div>
                ) : coverageFlags.length === 0 ? (
                  <div className="text-sm text-gray-500 py-6 text-center">No coverage data for this window.</div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Episode
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vera Verdict
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Confidence
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Timestamp
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {coverageFlags.map((flag) => {
                        const verdict =
                          flag.failure_mode === "correct"
                            ? "correct"
                            : flag.failure_mode === "unnamed"
                            ? "unnamed"
                            : flag.corrected_speaker || flag.failure_mode;
                        return (
                          <tr key={flag.id}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                {flag.thumbnail_url && (
                                  <div className="flex-shrink-0 w-8 h-8 rounded overflow-hidden bg-gray-100">
                                    <Thumbnail
                                      src={flag.thumbnail_url}
                                      alt={flag.title ?? ""}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                )}
                                <a
                                  href={`/transcript/${flag.item_id}${flag.first_appearance_ms ? `?t=${flag.first_appearance_ms}` : ""}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:text-indigo-800 hover:underline truncate max-w-xs block"
                                >
                                  {flag.title}
                                </a>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                                  flag.failure_mode === "correct"
                                    ? "bg-green-100 text-green-700"
                                    : flag.failure_mode === "unnamed"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                {verdict}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-500">
                              {flag.confidence !== null && flag.confidence !== undefined
                                ? `${(flag.confidence * 100).toFixed(0)}%`
                                : "—"}
                            </td>
                            <td className="px-3 py-2">
                              {flag.first_appearance_ms != null ? (
                                <a
                                  href={`/transcript/${flag.item_id}?t=${flag.first_appearance_ms}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs font-mono text-gray-400 hover:text-indigo-600 bg-gray-100 hover:bg-indigo-50 px-1.5 py-0.5 rounded"
                                >
                                  {formatTimestamp(flag.first_appearance_ms)}
                                </a>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
